'use client';

import {
  JS_VIEW_SCREEN_EVENT_NAME,
  UPZI_LOGIN_SUCCESS_EVENT_NAME,
  UPZI_LOGOUT_EVENT_NAME,
  UPZI_SESSION_EXPIRED_EVENT_NAME,
} from '@/common';
import dynamic from 'next/dynamic';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SeoLinkGroup } from '@/components/seo/seo-link-groups';
import { pushApplyStart } from '@/lib/gtm';
import { cn } from '@/lib/utils';
import {
  getApplicantTrackingProperties,
  getFilterTrackingProperties,
  getQuickResponseTrackingProperties,
  getSaveOpportunityTrackingProperties,
  getSearchTrackingProperties,
  useOpportunityTracking,
} from '@/hooks/use-opportunity-tracking';
import { usePosthog } from '@/hooks/use-posthog';
import {
  applyJobSalaryMarketEvaluations,
  buildJobSearchPayload,
  DEFAULT_JOB_FILTERS,
  getJobDetail,
  getJobSalaryMarketEvaluations,
  getJobSearchPath,
  isValidJobId,
  JOBS_FIRST_PAGE,
  JOBS_PAGE_SIZE,
  searchJobs,
} from '@/services/jobs';
import { useSavedJobs } from '@/services/jobs/use-saved-jobs';
import type {
  JobDetail,
  JobFilters,
  JobFilterOrderItem,
  JobFilterState,
  JobMultiFilterKey,
  JobPagination,
  JobSearchResponse,
  JobSingleFilterKey,
  JobSummary,
} from '@/services/jobs/model';
import { JobListEmpty } from './components/job-list-empty';
import { JobResults } from './components/job-results';
import type { JobResultsViewMode } from './components/job-results-view-toggle';
import type { JobSearchInputHandle } from './components/job-search-hero';
import { JobSearchControls } from './components/job-search-controls';
import { JobSearchMetadataSync } from './components/job-search-metadata-sync';
import { useDesktopSplitView } from './hooks/use-desktop-split-view';
import { isJobInactive } from './utils';
import {
  OPPORTUNITY_TRACKING_EVENTS,
  type SearchMethod,
} from '@/services/opportunity-tracking';
import {
  consumeJobSearchLoadingTime,
  consumeJobSearchMethod,
  consumeJobSearchSourceScreen,
  getJobPreviewEntrySourceScreen,
  SOURCE_SCREENS,
} from '@/services/opportunity-tracking/source-screen';

const JobSearchLiveSeoSection = dynamic(() =>
  import('./components/job-search-live-seo-section').then(
    module => module.JobSearchLiveSeoSection
  )
);

const JobDetailPreview = dynamic(
  () =>
    import('./components/job-detail-preview').then(
      module => module.JobDetailPreview
    ),
  { ssr: false }
);
const MobileJobDetailDrawer = dynamic(
  () =>
    import('./components/mobile-job-detail-drawer').then(
      module => module.MobileJobDetailDrawer
    ),
  { ssr: false }
);

type JobBoardProps = {
  initialSeoSection: ReactNode;
  initialAddress?: string;
  initialDesktopSplitView?: boolean;
  initialDesktopJobFilters?: boolean;
  initialDetail: JobDetail | null;
  initialDetailInactive?: boolean;
  initialDetailUnavailable?: boolean;
  initialFilters?: Partial<JobFilterState>;
  initialQuery?: string;
  initialResponse: JobSearchResponse | null;
  isLoggedIn: boolean;
  previewJobId?: string;
  seoLinkGroupsPromise: Promise<ReadonlyArray<SeoLinkGroup>>;
};

type JobSearchOptions = {
  preserveSelectedJob?: boolean;
  query?: string;
  scrollToResultsTop?: boolean;
  searchMethod?: SearchMethod;
  trackingAction?: 'filter' | 'result' | 'search';
  urlPath?: string;
};

const FILTER_SEARCH_OPTIONS: JobSearchOptions = {
  preserveSelectedJob: true,
  trackingAction: 'filter',
};
const RESULT_SEARCH_OPTIONS: JobSearchOptions = {
  scrollToResultsTop: true,
  trackingAction: 'result',
};

const DEFAULT_PAGINATION: JobPagination = {
  page: JOBS_FIRST_PAGE,
  total: 0,
  pageSize: JOBS_PAGE_SIZE,
  totalPages: 1,
};

const DEFAULT_FILTER_OPTIONS: Partial<JobFilters> = {};
const DEFAULT_FILTER_ORDER: JobFilterOrderItem[] = [];
const PREVIEW_QUERY_PARAM = 'preview';
const MAX_JOB_CACHE_ENTRIES = 50;

function getCachedValue<TKey, TValue>(
  cache: Map<TKey, TValue>,
  key: TKey
): TValue | undefined {
  const value = cache.get(key);

  if (value === undefined) return undefined;

  cache.delete(key);
  cache.set(key, value);

  return value;
}

function setCachedValue<TKey, TValue>(
  cache: Map<TKey, TValue>,
  key: TKey,
  value: TValue
) {
  cache.delete(key);
  cache.set(key, value);

  while (cache.size > MAX_JOB_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;

    if (oldestKey === undefined) break;

    cache.delete(oldestKey);
  }
}

function getReadableJobSearchUrl(url: URL): string {
  return url.href.replaceAll('%2C', ',');
}

function updatePreviewUrl(
  jobId: string | undefined,
  mode: 'push' | 'replace' = 'replace'
) {
  const url = new URL(window.location.href);

  url.searchParams.delete('job');

  if (jobId) {
    url.searchParams.set(PREVIEW_QUERY_PARAM, jobId);
  } else {
    url.searchParams.delete(PREVIEW_QUERY_PARAM);
  }

  const nextState = {
    ...window.history.state,
    jobPreviewId: jobId ?? null,
  };
  const nextUrl = getReadableJobSearchUrl(url);

  if (mode === 'push') {
    window.history.pushState(nextState, '', nextUrl);
  } else {
    window.history.replaceState(nextState, '', nextUrl);
  }
}

function updateJobSearchUrl(
  jobId: string | undefined,
  query: string,
  address: string,
  filters: JobFilterState,
  page: number,
  urlPath?: string
) {
  const url = new URL(
    urlPath ?? getJobSearchPath(query, address, filters, page),
    window.location.origin
  );

  url.searchParams.delete('job');

  if (jobId) url.searchParams.set(PREVIEW_QUERY_PARAM, jobId);

  window.history.replaceState(
    {
      ...window.history.state,
      jobPreviewId: jobId ?? null,
    },
    '',
    getReadableJobSearchUrl(url)
  );
}

export default function JobBoard({
  initialSeoSection,
  initialAddress = '',
  initialDesktopSplitView = false,
  initialDesktopJobFilters = false,
  initialDetail,
  initialDetailInactive = false,
  initialDetailUnavailable = false,
  initialFilters,
  initialQuery = '',
  initialResponse,
  isLoggedIn,
  previewJobId,
  seoLinkGroupsPromise,
}: JobBoardProps) {
  const { trackOpportunityEvent } = useOpportunityTracking();
  const { track } = usePosthog();
  const isDesktopSplitView = useDesktopSplitView(initialDesktopSplitView);
  const initialJobs = initialResponse?.data ?? [];
  const firstSelectedJobId = initialDetail?.id ?? previewJobId ?? null;
  const [query, setQuery] = useState(initialQuery);
  const [searchedQuery, setSearchedQuery] = useState(initialQuery.trim());
  const [address, setAddress] = useState(initialAddress);
  const [filters, setFilters] = useState<JobFilterState>(() => ({
    ...DEFAULT_JOB_FILTERS,
    ...initialFilters,
  }));
  const [jobs, setJobs] = useState<JobSummary[]>(initialJobs);
  const [filterOptions, setFilterOptions] = useState<Partial<JobFilters>>(
    initialResponse?.meta.filter ?? DEFAULT_FILTER_OPTIONS
  );
  const addressOptions = useMemo(
    () => filterOptions.location ?? [],
    [filterOptions.location]
  );
  const [filterOrder, setFilterOrder] = useState<JobFilterOrderItem[]>(
    initialResponse?.meta.filterOrder ?? DEFAULT_FILTER_ORDER
  );
  const [pagination, setPagination] = useState<JobPagination>(
    initialResponse?.meta.pagination ?? DEFAULT_PAGINATION
  );
  const [selectedJobId, setSelectedJobId] = useState<string | null>(
    firstSelectedJobId
  );
  const [scrollTargetJobId, setScrollTargetJobId] = useState<string | null>(
    null
  );
  const [jobDetail, setJobDetail] = useState<JobDetail | null>(initialDetail);
  const [detailUnavailableJobId, setDetailUnavailableJobId] = useState<
    string | null
  >(initialDetailUnavailable ? (previewJobId ?? null) : null);
  const [detailInactiveJobId, setDetailInactiveJobId] = useState<string | null>(
    initialDetailInactive ? (previewJobId ?? null) : null
  );
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMoreJobs, setIsLoadingMoreJobs] = useState(false);
  const [infiniteScrollError, setInfiniteScrollError] = useState('');
  const [hasMoreJobs, setHasMoreJobs] = useState(
    initialResponse?.meta.pagination.page === JOBS_FIRST_PAGE &&
      initialJobs.length <
        Math.min(JOBS_PAGE_SIZE, initialResponse?.meta.pagination.total ?? 0)
  );
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [fixedFilterHeight, setFixedFilterHeight] = useState(0);
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);
  const [jobResultsViewMode, setJobResultsViewMode] =
    useState<JobResultsViewMode>('list');
  const [clientAuthOverride, setClientAuthOverride] = useState<boolean | null>(
    null
  );
  const [authSessionVersion, setAuthSessionVersion] = useState(0);
  const isAuthenticated = clientAuthOverride ?? isLoggedIn;
  const { checkSavedJobs, pendingJobId, savedJobIds, toggleSavedJob } =
    useSavedJobs({
      enabled: isAuthenticated,
      fetchOnMount: false,
      sourceScreen: SOURCE_SCREENS.SEARCH_RESULT,
    });
  const savedJobCheckKey = useMemo(
    () =>
      [
        ...new Set([
          ...jobs.map(job => job.id),
          ...(selectedJobId ? [selectedJobId] : []),
        ]),
      ]
        .sort()
        .join(','),
    [jobs, selectedJobId]
  );
  const [errorMessage, setErrorMessage] = useState(
    initialResponse ? '' : 'Không thể tải danh sách việc làm lúc này.'
  );
  const detailRequestRef = useRef(0);
  const searchRequestRef = useRef(0);
  const searchJobsRequestRef = useRef<AbortController | null>(null);
  const loadMoreJobsRequestRef = useRef<AbortController | null>(null);
  const scrollAnimationFrameRef = useRef<number | null>(null);
  const initialSalaryMarketRequestRef = useRef<string | null>(null);
  const detailCacheRef = useRef(
    new Map<string, JobDetail>(
      initialDetail ? [[initialDetail.id, initialDetail]] : []
    )
  );
  const detailBodyScrollRef = useRef<HTMLDivElement>(null);
  const queryInputRef = useRef<JobSearchInputHandle>(null);
  const resultsTopRef = useRef<HTMLDivElement>(null);
  const hasInitializedUrlRef = useRef(false);
  const hasRefetchedAfterLoginRef = useRef(isLoggedIn);
  const hasTrackedInitialResultsRef = useRef(false);
  const initialSourceScreenRef = useRef<string | undefined>(undefined);
  const initialSearchMethodRef = useRef<SearchMethod | undefined>(undefined);
  const initialLoadingTimeRef = useRef('0');
  const [searchMethod, setSearchMethod] = useState<SearchMethod | undefined>();
  const latestQueryRef = useRef(query);
  const latestSearchMethodRef = useRef(searchMethod);

  const handleQueryChange = useCallback((nextQuery: string) => {
    latestQueryRef.current = nextQuery;
  }, []);

  const trackPreviewScreenView = useCallback(() => {
    track(JS_VIEW_SCREEN_EVENT_NAME, {
      source_screen: getJobPreviewEntrySourceScreen(),
    });
  }, [track]);

  useEffect(() => {
    initialSourceScreenRef.current = consumeJobSearchSourceScreen();
    initialSearchMethodRef.current = consumeJobSearchMethod();
    initialLoadingTimeRef.current = consumeJobSearchLoadingTime();
    latestSearchMethodRef.current = initialSearchMethodRef.current;
    setSearchMethod(initialSearchMethodRef.current);
  }, []);

  useEffect(() => {
    if (hasTrackedInitialResultsRef.current || !initialResponse) return;

    hasTrackedInitialResultsRef.current = true;
    void trackOpportunityEvent({
      eventName: OPPORTUNITY_TRACKING_EVENTS.viewResult,
      includeUserInfo: isAuthenticated,
      properties: {
        loading_time: initialLoadingTimeRef.current,
        number_of_result: initialResponse.meta.pagination.total,
        ...getSearchTrackingProperties(
          initialQuery,
          initialSearchMethodRef.current
        ),
        ...(initialSourceScreenRef.current?.startsWith('js_')
          ? { source_screen: initialSourceScreenRef.current }
          : {}),
      },
    });
  }, [initialQuery, initialResponse, isAuthenticated, trackOpportunityEvent]);

  useEffect(() => {
    if (!isAuthenticated || !initialResponse) return;

    const jobIds = [
      ...initialResponse.data.map(job => job.id),
      ...(initialDetail ? [initialDetail.id] : []),
    ];
    const initialJobIdSet = new Set(jobIds);
    const requestKey = [...new Set(jobIds)].sort().join(',');

    if (!requestKey || initialSalaryMarketRequestRef.current === requestKey) {
      return;
    }

    initialSalaryMarketRequestRef.current = requestKey;
    const requestId = searchRequestRef.current;

    void getJobSalaryMarketEvaluations(jobIds)
      .then(evaluations => {
        if (searchRequestRef.current !== requestId) return;

        setJobs(currentJobs =>
          applyJobSalaryMarketEvaluations(currentJobs, evaluations)
        );
        setJobDetail(currentDetail =>
          currentDetail && initialJobIdSet.has(currentDetail.id)
            ? applyJobSalaryMarketEvaluations([currentDetail], evaluations)[0]
            : currentDetail
        );
      })
      .catch(error => {
        console.error(
          '[jobs/salary-market] Unable to load initial evaluations',
          error
        );
      });
  }, [initialDetail, initialResponse, isAuthenticated]);

  const scrollToResultsTop = useCallback(() => {
    const resultsTopElement = resultsTopRef.current;

    if (!resultsTopElement) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    resultsTopElement.scrollIntoView({
      block: 'start',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, []);

  useEffect(
    () => () => {
      searchJobsRequestRef.current?.abort();
      loadMoreJobsRequestRef.current?.abort();

      if (scrollAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollAnimationFrameRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const handleLoginSuccess = () => {
      setClientAuthOverride(true);
      setAuthSessionVersion(version => version + 1);
    };
    const handleLogout = () => {
      setClientAuthOverride(false);
      setAuthSessionVersion(version => version + 1);
    };

    window.addEventListener(UPZI_LOGIN_SUCCESS_EVENT_NAME, handleLoginSuccess);
    window.addEventListener(UPZI_LOGOUT_EVENT_NAME, handleLogout);
    window.addEventListener(UPZI_SESSION_EXPIRED_EVENT_NAME, handleLogout);

    return () => {
      window.removeEventListener(
        UPZI_LOGIN_SUCCESS_EVENT_NAME,
        handleLoginSuccess
      );
      window.removeEventListener(UPZI_LOGOUT_EVENT_NAME, handleLogout);
      window.removeEventListener(UPZI_SESSION_EXPIRED_EVENT_NAME, handleLogout);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const controller = new AbortController();
    const jobIds = savedJobCheckKey ? savedJobCheckKey.split(',') : [];

    void checkSavedJobs(jobIds, {
      replace: true,
      signal: controller.signal,
    });

    return () => controller.abort();
  }, [checkSavedJobs, isAuthenticated, savedJobCheckKey]);

  useEffect(() => {
    if (hasInitializedUrlRef.current) return;

    hasInitializedUrlRef.current = true;

    if (firstSelectedJobId) {
      updatePreviewUrl(firstSelectedJobId, 'replace');
    }
  }, [firstSelectedJobId]);

  const loadJobDetail = useCallback(
    async (
      jobId: string,
      options: { force?: boolean; preserveScroll?: boolean } = {}
    ) => {
      const requestId = detailRequestRef.current + 1;
      const cachedDetail = getCachedValue(detailCacheRef.current, jobId);

      detailRequestRef.current = requestId;
      setSelectedJobId(jobId);
      setDetailUnavailableJobId(null);
      setDetailInactiveJobId(null);
      if (!options.preserveScroll) {
        detailBodyScrollRef.current?.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }

      if (cachedDetail && !options.force) {
        setJobDetail(cachedDetail);
        setIsLoadingDetail(false);

        return;
      }

      setJobDetail(null);
      setIsLoadingDetail(true);
      const detailPromise = getJobDetail(jobId);
      const salaryMarketPromise = isAuthenticated
        ? getJobSalaryMarketEvaluations([jobId]).catch(error => {
            console.error(
              '[jobs/salary-market] Unable to load preview evaluation',
              error
            );

            return {};
          })
        : null;

      try {
        const result = await detailPromise;

        if (
          isJobInactive({ status: result.status }) ||
          (result.data && isJobInactive(result.data))
        ) {
          if (detailRequestRef.current === requestId) {
            setJobDetail(null);
            setDetailInactiveJobId(jobId);
          }

          return;
        }

        if (detailRequestRef.current === requestId) {
          setCachedValue(detailCacheRef.current, jobId, result.data);
          setJobDetail(result.data);
        }

        if (salaryMarketPromise) {
          void salaryMarketPromise.then(salaryMarketEvaluations => {
            const cachedDetail =
              getCachedValue(detailCacheRef.current, jobId) ?? result.data;
            const [enrichedDetail] = applyJobSalaryMarketEvaluations(
              [cachedDetail],
              salaryMarketEvaluations
            );

            setCachedValue(detailCacheRef.current, jobId, enrichedDetail);
            if (detailRequestRef.current === requestId) {
              setJobDetail(currentDetail =>
                currentDetail?.id === jobId
                  ? applyJobSalaryMarketEvaluations(
                      [currentDetail],
                      salaryMarketEvaluations
                    )[0]
                  : currentDetail
              );
            }
          });
        }
      } catch {
        if (detailRequestRef.current === requestId) {
          setDetailInactiveJobId(null);
          setDetailUnavailableJobId(jobId);
        }
      } finally {
        if (detailRequestRef.current === requestId) {
          setIsLoadingDetail(false);
        }
      }
    },
    [isAuthenticated]
  );

  const handleSearch = useCallback(
    async (
      page = JOBS_FIRST_PAGE,
      nextFilters: JobFilterState = filters,
      nextAddress = address,
      options: JobSearchOptions = {}
    ) => {
      const requestId = searchRequestRef.current + 1;
      const controller = new AbortController();
      const shouldPreserveSelectedJob = Boolean(
        options.preserveSelectedJob && selectedJobId
      );
      const requestQuery = options.query ?? latestQueryRef.current;
      const requestSearchMethod =
        options.searchMethod ?? latestSearchMethodRef.current;
      const requestStartedAt = performance.now();
      const shouldScrollToResultsTop =
        options.scrollToResultsTop ||
        (options.trackingAction === 'filter' && fixedFilterHeight > 0);

      searchJobsRequestRef.current?.abort();
      searchJobsRequestRef.current = controller;
      loadMoreJobsRequestRef.current?.abort();
      loadMoreJobsRequestRef.current = null;
      setIsLoadingMoreJobs(false);
      setInfiniteScrollError('');
      setHasMoreJobs(false);
      searchRequestRef.current = requestId;
      setQuery(requestQuery);
      setIsSearching(true);
      setErrorMessage('');
      if (options.searchMethod) {
        latestSearchMethodRef.current = options.searchMethod;
        setSearchMethod(options.searchMethod);
      }

      if (options.trackingAction === 'search') {
        void trackOpportunityEvent({
          eventName: OPPORTUNITY_TRACKING_EVENTS.search,
          includeUserInfo: isAuthenticated,
          properties: {
            ...getSearchTrackingProperties(requestQuery, requestSearchMethod),
            source_screen: SOURCE_SCREENS.SEARCH_RESULT,
            source_section: 'hero_section',
          },
        });
      }

      if (shouldScrollToResultsTop) {
        if (scrollAnimationFrameRef.current !== null) {
          cancelAnimationFrame(scrollAnimationFrameRef.current);
        }

        scrollAnimationFrameRef.current = requestAnimationFrame(() => {
          scrollAnimationFrameRef.current = null;
          scrollToResultsTop();
        });
      }

      if (!shouldPreserveSelectedJob) {
        detailRequestRef.current += 1;
        setIsMobileDetailOpen(false);
        setScrollTargetJobId(null);
        setSelectedJobId(null);
        setJobDetail(null);
        setDetailUnavailableJobId(null);
        setDetailInactiveJobId(null);
        setIsLoadingDetail(false);
      }

      updateJobSearchUrl(
        shouldPreserveSelectedJob ? (selectedJobId ?? undefined) : undefined,
        requestQuery,
        nextAddress,
        nextFilters,
        page,
        options.urlPath
      );

      try {
        const result = await searchJobs(
          buildJobSearchPayload(requestQuery, nextFilters, page, filterOptions),
          controller.signal
        );

        if (
          controller.signal.aborted ||
          searchRequestRef.current !== requestId
        ) {
          return;
        }

        setJobs(result.data);
        setSearchedQuery(requestQuery.trim());
        setPagination(result.meta.pagination);
        setFilterOptions(result.meta.filter);
        setFilterOrder(result.meta.filterOrder ?? DEFAULT_FILTER_ORDER);
        setHasMoreJobs(false);

        const searchProperties = {
          number_of_result: result.meta.pagination.total,
          ...getSearchTrackingProperties(requestQuery, requestSearchMethod),
          source_screen: SOURCE_SCREENS.SEARCH_RESULT,
        };

        if (options.trackingAction === 'filter') {
          void trackOpportunityEvent({
            eventName: OPPORTUNITY_TRACKING_EVENTS.filter,
            properties: getFilterTrackingProperties(
              nextFilters,
              result.meta.filter
            ),
          });
        }

        if (options.trackingAction) {
          void trackOpportunityEvent({
            eventName: OPPORTUNITY_TRACKING_EVENTS.viewResult,
            includeUserInfo: isAuthenticated,
            properties: {
              ...searchProperties,
              loading_time: String(
                (performance.now() - requestStartedAt) / 1000
              ),
            },
          });
        }

        if (isAuthenticated && result.data.length > 0) {
          void getJobSalaryMarketEvaluations(result.data.map(job => job.id))
            .then(salaryMarketEvaluations => {
              if (searchRequestRef.current === requestId) {
                setJobs(
                  applyJobSalaryMarketEvaluations(
                    result.data,
                    salaryMarketEvaluations
                  )
                );
              }
            })
            .catch(error => {
              console.error(
                '[jobs/salary-market] Unable to load search evaluations',
                error
              );
            });
        }

        if (shouldPreserveSelectedJob) {
          detailRequestRef.current += 1;
        }

        const preservedJobId =
          shouldPreserveSelectedJob &&
          selectedJobId &&
          result.data.some(job => job.id === selectedJobId)
            ? selectedJobId
            : null;
        const nextJobId = shouldPreserveSelectedJob
          ? (preservedJobId ?? result.data[0]?.id ?? null)
          : null;

        setSelectedJobId(nextJobId);
        setJobDetail(null);
        setDetailUnavailableJobId(null);
        setDetailInactiveJobId(null);
        setIsLoadingDetail(false);
        updateJobSearchUrl(
          nextJobId ?? undefined,
          requestQuery,
          nextAddress,
          nextFilters,
          page,
          options.urlPath
        );
        if (!options.preserveSelectedJob) {
          detailBodyScrollRef.current?.scrollTo({
            top: 0,
            behavior: 'instant',
          });
        }

        if (nextJobId) {
          void loadJobDetail(nextJobId, {
            force: options.preserveSelectedJob,
            preserveScroll: preservedJobId === nextJobId,
          });
        }
      } catch {
        if (
          !controller.signal.aborted &&
          searchRequestRef.current === requestId
        ) {
          setErrorMessage(
            'Không thể tải danh sách việc làm. Vui lòng thử lại.'
          );
          if (options.trackingAction) {
            void trackOpportunityEvent({
              eventName: OPPORTUNITY_TRACKING_EVENTS.viewResult,
              includeUserInfo: isAuthenticated,
              properties: {
                error_code: 999,
                loading_time: String(
                  (performance.now() - requestStartedAt) / 1000
                ),
                number_of_result: 0,
                ...getSearchTrackingProperties(
                  requestQuery,
                  requestSearchMethod
                ),
                source_screen: SOURCE_SCREENS.SEARCH_RESULT,
              },
            });
          }
        }
      } finally {
        if (searchJobsRequestRef.current === controller) {
          searchJobsRequestRef.current = null;
        }

        if (
          !controller.signal.aborted &&
          searchRequestRef.current === requestId
        ) {
          setIsSearching(false);
        }
      }
    },
    [
      address,
      filterOptions,
      filters,
      fixedFilterHeight,
      isAuthenticated,
      loadJobDetail,
      scrollToResultsTop,
      selectedJobId,
      trackOpportunityEvent,
    ]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      hasRefetchedAfterLoginRef.current = false;

      return;
    }

    if (hasRefetchedAfterLoginRef.current) return;

    hasRefetchedAfterLoginRef.current = true;
    detailCacheRef.current.clear();
    void handleSearch(pagination.page, filters, address, {
      preserveSelectedJob: true,
      query: searchedQuery,
    });
  }, [
    address,
    filters,
    handleSearch,
    isAuthenticated,
    pagination.page,
    searchedQuery,
  ]);

  const handleLocationChange = useCallback(
    (locationIds: string[], locationNames: string[]) => {
      const nextAddress = locationNames.join(', ');
      const nextFilters = {
        ...filters,
        location: locationIds,
      };

      setAddress(nextAddress);
      setFilters(nextFilters);
      void handleSearch(
        JOBS_FIRST_PAGE,
        nextFilters,
        nextAddress,
        FILTER_SEARCH_OPTIONS
      );
    },
    [filters, handleSearch]
  );

  const handleMultiFilterChange = useCallback(
    (key: JobMultiFilterKey, value: string[]) => {
      if (key === 'location') {
        const addressById = new Map(
          addressOptions.map(option => [String(option.id), option.value])
        );

        handleLocationChange(
          value,
          value.flatMap(locationId => {
            const selectedAddress = addressById.get(locationId);

            return selectedAddress ? [selectedAddress] : [];
          })
        );

        return;
      }

      const nextFilters = {
        ...filters,
        [key]: value,
      };

      setFilters(nextFilters);
      void handleSearch(
        JOBS_FIRST_PAGE,
        nextFilters,
        address,
        FILTER_SEARCH_OPTIONS
      );
    },
    [address, addressOptions, filters, handleLocationChange, handleSearch]
  );

  const handleClearFilters = useCallback(() => {
    const nextFilters = { ...DEFAULT_JOB_FILTERS };

    setAddress('');
    setFilters(nextFilters);
    void handleSearch(JOBS_FIRST_PAGE, nextFilters, '', FILTER_SEARCH_OPTIONS);
  }, [handleSearch]);

  const handleFiltersChange = useCallback(
    (nextFilters: JobFilterState) => {
      const addressById = new Map(
        addressOptions.map(option => [String(option.id), option.value])
      );
      const nextAddress = nextFilters.location
        .flatMap(locationId => {
          const selectedAddress = addressById.get(locationId);

          return selectedAddress ? [selectedAddress] : [];
        })
        .join(', ');

      setAddress(nextAddress);
      setFilters(nextFilters);
      void handleSearch(
        JOBS_FIRST_PAGE,
        nextFilters,
        nextAddress,
        FILTER_SEARCH_OPTIONS
      );
    },
    [addressOptions, handleSearch]
  );

  const handleSingleFilterChange = useCallback(
    (key: JobSingleFilterKey, value: string) => {
      const nextFilters = { ...filters, [key]: value };

      setFilters(nextFilters);
      void handleSearch(
        JOBS_FIRST_PAGE,
        nextFilters,
        address,
        FILTER_SEARCH_OPTIONS
      );
    },
    [address, filters, handleSearch]
  );

  const handlePageChange = useCallback(
    (page: number, href: string) => {
      void handleSearch(page, filters, address, {
        ...RESULT_SEARCH_OPTIONS,
        urlPath: href,
      });
    },
    [address, filters, handleSearch]
  );

  const handleSearchSubmit = useCallback(
    (submittedQuery: string) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      void handleSearch(JOBS_FIRST_PAGE, filters, address, {
        preserveSelectedJob: true,
        query: submittedQuery,
        searchMethod: 'type',
        trackingAction: 'search',
      });
    },
    [address, filters, handleSearch]
  );

  const handleRetrySearch = useCallback(() => {
    void handleSearch(JOBS_FIRST_PAGE, filters, address, RESULT_SEARCH_OPTIONS);
  }, [address, filters, handleSearch]);

  const handleLoadMoreJobs = useCallback(async () => {
    if (
      loadMoreJobsRequestRef.current ||
      isSearching ||
      !hasMoreJobs ||
      pagination.page !== JOBS_FIRST_PAGE
    ) {
      return;
    }

    const controller = new AbortController();
    const searchRequestId = searchRequestRef.current;

    loadMoreJobsRequestRef.current = controller;
    setIsLoadingMoreJobs(true);
    setInfiniteScrollError('');

    try {
      const result = await searchJobs(
        buildJobSearchPayload(
          searchedQuery,
          filters,
          pagination.page,
          filterOptions,
          JOBS_PAGE_SIZE
        ),
        controller.signal
      );

      if (
        controller.signal.aborted ||
        searchRequestRef.current !== searchRequestId
      ) {
        return;
      }

      setHasMoreJobs(false);
      setPagination(result.meta.pagination);
      setFilterOptions(result.meta.filter);
      setFilterOrder(result.meta.filterOrder ?? DEFAULT_FILTER_ORDER);
      setJobs(result.data);

      if (isAuthenticated) {
        if (result.data.length > 0) {
          void getJobSalaryMarketEvaluations(result.data.map(job => job.id))
            .then(salaryMarketEvaluations => {
              if (searchRequestRef.current === searchRequestId) {
                setJobs(currentJobs =>
                  applyJobSalaryMarketEvaluations(
                    currentJobs,
                    salaryMarketEvaluations
                  )
                );
              }
            })
            .catch(error => {
              console.error(
                '[jobs/salary-market] Unable to load infinite-scroll evaluations',
                error
              );
            });
        }
      }
    } catch (error) {
      if (
        !controller.signal.aborted &&
        searchRequestRef.current === searchRequestId
      ) {
        console.error('[jobs/search] Unable to load more jobs', error);
        setInfiniteScrollError(
          'Không thể tải thêm việc làm. Vui lòng thử lại.'
        );
      }
    } finally {
      if (loadMoreJobsRequestRef.current === controller) {
        loadMoreJobsRequestRef.current = null;
        setIsLoadingMoreJobs(false);
      }
    }
  }, [
    filterOptions,
    filters,
    hasMoreJobs,
    isAuthenticated,
    isSearching,
    pagination.page,
    searchedQuery,
  ]);

  const handleSelectJob = useCallback(
    (job: JobSummary) => {
      setScrollTargetJobId(null);

      if (!isDesktopSplitView) {
        trackPreviewScreenView();
        setIsMobileDetailOpen(true);
        updatePreviewUrl(job.id, 'push');
        void loadJobDetail(job.id, {
          force: detailUnavailableJobId === job.id,
        });

        return;
      }

      if (selectedJobId === job.id && jobDetail?.id === job.id) return;

      trackPreviewScreenView();
      updatePreviewUrl(job.id, 'push');
      void loadJobDetail(job.id, {
        force: detailUnavailableJobId === job.id,
      });
    },
    [
      detailUnavailableJobId,
      isDesktopSplitView,
      jobDetail?.id,
      loadJobDetail,
      selectedJobId,
      trackPreviewScreenView,
    ]
  );

  const handleClosePreview = useCallback(() => {
    detailRequestRef.current += 1;
    setIsMobileDetailOpen(false);
    setScrollTargetJobId(selectedJobId);
    setSelectedJobId(null);
    setJobDetail(null);
    setDetailUnavailableJobId(null);
    setDetailInactiveJobId(null);
    setIsLoadingDetail(false);
    updatePreviewUrl(undefined, 'push');
  }, [selectedJobId]);

  const handleScrollTargetHandled = useCallback(() => {
    setScrollTargetJobId(null);
  }, []);

  const handleSaveChange = useCallback(
    (job: JobSummary) => {
      void toggleSavedJob(job);
    },
    [toggleSavedJob]
  );

  const handlePreviewSaveChange = useCallback(
    (job: JobSummary) => {
      void toggleSavedJob(job, {
        sourceScreen: SOURCE_SCREENS.JOB_PREVIEW,
      });
    },
    [toggleSavedJob]
  );

  const handleGuestSaveChange = useCallback(
    (job: JobSummary) => {
      return trackOpportunityEvent({
        eventName: OPPORTUNITY_TRACKING_EVENTS.save,
        includeUserInfo: false,
        jobId: job.id,
        properties: getSaveOpportunityTrackingProperties(
          SOURCE_SCREENS.SEARCH_RESULT
        ),
      });
    },
    [trackOpportunityEvent]
  );

  const handlePreviewGuestSaveChange = useCallback(
    (job: JobSummary) => {
      return trackOpportunityEvent({
        eventName: OPPORTUNITY_TRACKING_EVENTS.save,
        includeUserInfo: false,
        jobId: job.id,
        properties: getSaveOpportunityTrackingProperties(
          SOURCE_SCREENS.JOB_PREVIEW
        ),
      });
    },
    [trackOpportunityEvent]
  );

  const isJobSavePending = useCallback(
    (jobId: string) => pendingJobId === jobId,
    [pendingJobId]
  );

  useEffect(() => {
    const handlePopState = () => {
      const requestedJobId = new URL(window.location.href).searchParams.get(
        PREVIEW_QUERY_PARAM
      );
      const nextJobId =
        requestedJobId && isValidJobId(requestedJobId) ? requestedJobId : null;

      if (nextJobId) {
        trackPreviewScreenView();
        setIsMobileDetailOpen(!isDesktopSplitView);
        void loadJobDetail(nextJobId);

        return;
      }

      detailRequestRef.current += 1;
      setIsMobileDetailOpen(false);
      setSelectedJobId(nextJobId);
      setDetailUnavailableJobId(null);
      setDetailInactiveJobId(null);
      setIsLoadingDetail(false);
      detailBodyScrollRef.current?.scrollTo({
        top: 0,
        behavior: 'instant',
      });
      setScrollTargetJobId(selectedJobId);
      setJobDetail(null);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [
    isDesktopSplitView,
    loadJobDetail,
    selectedJobId,
    trackPreviewScreenView,
  ]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsMobileDetailOpen(false);
      }

      if (
        event.matches &&
        selectedJobId &&
        jobDetail?.id !== selectedJobId &&
        detailUnavailableJobId !== selectedJobId &&
        detailInactiveJobId !== selectedJobId
      ) {
        void loadJobDetail(selectedJobId);
      }
    };

    mediaQuery.addEventListener('change', handleViewportChange);

    return () => mediaQuery.removeEventListener('change', handleViewportChange);
  }, [
    detailInactiveJobId,
    detailUnavailableJobId,
    jobDetail?.id,
    loadJobDetail,
    selectedJobId,
  ]);

  const retrySelectedJob = useCallback(() => {
    if (selectedJobId) void loadJobDetail(selectedJobId, { force: true });
  }, [loadJobDetail, selectedJobId]);
  const handleAdjustSearch = useCallback(() => {
    handleQueryChange('');
    setQuery('');
    queryInputRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [handleQueryChange]);
  const handleGuestApply = useCallback(
    (job: JobDetail) => {
      pushApplyStart(job, 'job_list');

      return trackOpportunityEvent({
        eventName: OPPORTUNITY_TRACKING_EVENTS.applyStart,
        includeUserInfo: false,
        jobId: job.id,
        properties: {
          ...getApplicantTrackingProperties(job),
          ...getQuickResponseTrackingProperties(job),
          opportunity_type: 'job',
          source_screen: SOURCE_SCREENS.JOB_PREVIEW,
        },
      });
    },
    [trackOpportunityEvent]
  );
  const hasSearchError = Boolean(errorMessage);
  const hasNoResults = !isSearching && !hasSearchError && jobs.length === 0;
  const isSplitView = isDesktopSplitView && selectedJobId !== null;
  const showSplitView = !hasSearchError && isSplitView;
  const searchLocations = useMemo(
    () =>
      address
        .split(',')
        .map(location => location.trim())
        .filter((location, index, locations) =>
          Boolean(location && locations.indexOf(location) === index)
        ),
    [address]
  );

  return (
    <div
      data-job-search-board
      className="text-on-surface min-h-full min-w-0 bg-white sm:overflow-x-clip"
    >
      <JobSearchMetadataSync
        hasError={hasSearchError}
        isSearching={isSearching}
        jobs={jobs}
        searchLocations={searchLocations}
        searchQuery={searchedQuery}
        seoUrlStateKey={`${JSON.stringify(filters)}:${pagination.page}`}
        total={pagination.total}
      />
      <JobSearchControls
        authSessionVersion={authSessionVersion}
        hasSearchError={hasSearchError}
        initialDesktopViewport={initialDesktopJobFilters}
        filterOptions={filterOptions}
        filterOrder={filterOrder}
        filters={filters}
        isAuthenticated={isAuthenticated}
        isSearching={isSearching}
        locationOptions={addressOptions}
        onClearFilters={handleClearFilters}
        onFixedHeightChange={setFixedFilterHeight}
        onFiltersChange={handleFiltersChange}
        onMultiFilterChange={handleMultiFilterChange}
        onQueryChange={handleQueryChange}
        onSearchSubmit={handleSearchSubmit}
        onSingleFilterChange={handleSingleFilterChange}
        query={query}
        queryInputRef={queryInputRef}
      />

      <main className="mx-auto w-full max-w-[1440px] min-w-0 px-0 py-0 pb-6 sm:px-6 sm:[overflow-anchor:none] lg:px-20 lg:pb-6">
        <div
          ref={resultsTopRef}
          className="h-px scroll-mt-[113px] sm:scroll-mt-0"
          aria-hidden
        />
        {hasNoResults ? (
          <JobListEmpty onAdjustSearch={handleAdjustSearch} />
        ) : (
          <div
            className={cn(
              'w-full items-start px-4 sm:px-0',
              !hasSearchError && 'pt-1 sm:mt-0 sm:pt-4',
              showSplitView
                ? 'grid grid-cols-1 gap-6 lg:grid-cols-[minmax(360px,5fr)_minmax(0,9fr)]'
                : undefined
            )}
          >
            <JobResults
              errorMessage={errorMessage}
              infiniteScrollError={infiniteScrollError}
              hasMoreJobs={hasMoreJobs}
              isSplitView={showSplitView}
              isSearching={isSearching}
              isLoadingMoreJobs={isLoadingMoreJobs}
              isSavePending={isJobSavePending}
              jobs={jobs}
              onPageChange={handlePageChange}
              onLoadMoreJobs={handleLoadMoreJobs}
              onRetry={handleRetrySearch}
              onSaveChange={
                isAuthenticated ? handleSaveChange : handleGuestSaveChange
              }
              onScrollTargetHandled={handleScrollTargetHandled}
              onSelectJob={handleSelectJob}
              onViewModeChange={setJobResultsViewMode}
              pagination={pagination}
              requiresLogin={!isAuthenticated}
              savedJobIds={savedJobIds}
              searchLocations={searchLocations}
              searchMethod={searchMethod}
              searchQuery={searchedQuery}
              scrollTargetJobId={scrollTargetJobId}
              selectedJobId={selectedJobId}
              viewMode={jobResultsViewMode}
            />
            {showSplitView ? (
              <JobDetailPreview
                detailBodyScrollRef={detailBodyScrollRef}
                detailInactiveJobId={detailInactiveJobId}
                detailUnavailableJobId={detailUnavailableJobId}
                fixedHeaderHeight={fixedFilterHeight}
                isLoadingDetail={isSearching || isLoadingDetail}
                isLoggedIn={isAuthenticated}
                isSavePending={
                  selectedJobId !== null && pendingJobId === selectedJobId
                }
                isSaved={
                  selectedJobId !== null && savedJobIds.has(selectedJobId)
                }
                jobDetail={jobDetail}
                onClose={handleClosePreview}
                onGuestApply={handleGuestApply}
                onRetry={retrySelectedJob}
                onSaveChange={
                  isAuthenticated
                    ? handlePreviewSaveChange
                    : handlePreviewGuestSaveChange
                }
                selectedJobId={selectedJobId}
              />
            ) : null}
          </div>
        )}
      </main>

      {!isDesktopSplitView && isMobileDetailOpen ? (
        <MobileJobDetailDrawer
          bodyScrollRef={detailBodyScrollRef}
          detailInactiveJobId={detailInactiveJobId}
          detailUnavailableJobId={detailUnavailableJobId}
          isLoadingDetail={isSearching || isLoadingDetail}
          isLoggedIn={isAuthenticated}
          isSavePending={
            selectedJobId !== null && pendingJobId === selectedJobId
          }
          isSaved={selectedJobId !== null && savedJobIds.has(selectedJobId)}
          jobDetail={jobDetail}
          onClose={handleClosePreview}
          onGuestApply={handleGuestApply}
          onRetry={retrySelectedJob}
          onSaveChange={
            isAuthenticated
              ? handlePreviewSaveChange
              : handlePreviewGuestSaveChange
          }
          open
          selectedJobId={selectedJobId}
        />
      ) : null}

      {!isSearching && !hasSearchError ? (
        searchedQuery === initialQuery.trim() &&
        address === initialAddress &&
        pagination.total === initialResponse?.meta.pagination.total ? (
          initialSeoSection
        ) : (
          <Suspense fallback={null}>
            <JobSearchLiveSeoSection
              keyword={searchedQuery}
              location={address}
              seoLinkGroupsPromise={seoLinkGroupsPromise}
              total={pagination.total}
            />
          </Suspense>
        )
      ) : null}
    </div>
  );
}
